import { forwardRef } from 'react';
import type { FunctionComponent, Ref, PropsWithoutRef } from 'react';

import type { BaseProps, ForwardProps } from '../../types';
import type { MenuItemProps } from '../Menu/Menu.types';

import SingleSelectInput from './SingleSelectInput/SingleSelectInput';
import MultiSelectInput from './MultiSelectInput/MultiSelectInput';
import type { MultiSelectInputProps } from './MultiSelectInput/MultiSelectInput.types';
import type ComboBoxProps from './ComboBox.types';
import type { Selected } from './ComboBox.types';

interface ComboBoxInputProps extends BaseProps {
  mode?: 'multi-select' | 'single-select';
  selected?: Selected | Selected[];
  value?: ComboBoxProps['value'];
  visual?: ComboBoxProps['visual'];
  onRemove?: (id: MenuItemProps['id'], index: number) => void;
  /** Callback on selected item pseudo focus */
  onSelectedFocus?: MultiSelectInputProps['onSelectedFocus'];
  /** Callback on selected item pseudo blur */
  onSelectedBlur?: MultiSelectInputProps['onSelectedBlur'];
  /** Pause evaluation of selected item descendants */
  pauseDescendantEvaluation?: MultiSelectInputProps['pauseDescendantEvaluation'];
}

const ComboBoxInput: FunctionComponent<ComboBoxInputProps & ForwardProps> = forwardRef(
  function ComboBoxInput(props: PropsWithoutRef<ComboBoxInputProps>, ref: Ref<HTMLInputElement>) {
    const {
      value,
      mode,
      selected,
      visual,
      onRemove,
      onSelectedFocus,
      onSelectedBlur,
      pauseDescendantEvaluation,
      ...restProps
    } = props;

    return mode === 'multi-select' ? (
      <MultiSelectInput
        selected={selected as Selected[]}
        value={value}
        ref={ref}
        onRemove={onRemove}
        onSelectedBlur={onSelectedBlur}
        onSelectedFocus={onSelectedFocus}
        pauseDescendantEvaluation={pauseDescendantEvaluation}
        {...restProps}
      />
    ) : (
      <SingleSelectInput
        ref={ref}
        selected={selected as Selected}
        value={value}
        visual={visual}
        {...restProps}
      />
    );
  }
);

export default ComboBoxInput;
